import { ChevronRight, Building2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { extractStructureState } from "@/types/structure";

interface MatrizSectionProps {
  company: any;
  onViewMatriz?: () => void;
}

/**
 * Componente para exibir informação da Matriz (para Filiais)
 * 
 * Mostra:
 * - Nome e CNPJ da Matriz
 * - Localização
 * - Botão para navegar para a Matriz
 */
export default function MatrizSection({
  company,
  onViewMatriz,
}: MatrizSectionProps) {
  const structureState = extractStructureState(company);

  // Se não for filial, não exibir
  if (!structureState.matrizId) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Matriz</CardTitle>
        <CardDescription>
          Informações da empresa matriz
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            Esta é uma filial. Filiais não podem ter filiais próprias e herdam o grupo econômico da matriz.
          </AlertDescription>
        </Alert>

        {/* Informações da Matriz */}
        <div className="border rounded-lg p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-sm text-muted-foreground">Matriz</p>
                <p className="font-semibold">
                  {company?.matrizNomeFantasia || "Carregando..."}
                </p>
              </div>
            </div>
            <Badge variant="outline">Matriz</Badge>
          </div>

          {/* CNPJ */}
          {company?.matrizCnpj && (
            <div>
              <p className="text-sm text-muted-foreground">CNPJ</p>
              <p className="font-mono text-sm">{company.matrizCnpj}</p>
            </div>
          )}

          {/* Localização */}
          {(company?.matrizCidade || company?.matrizEstado) && (
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {company?.matrizCidade && `${company.matrizCidade}`}
                {company?.matrizEstado && `, ${company.matrizEstado}`}
              </span>
            </div>
          )}

          {/* Botão para ver detalhes */}
          {onViewMatriz && (
            <Button
              variant="outline"
              size="sm"
              onClick={onViewMatriz}
              className="w-full"
            >
              Ver Detalhes da Matriz
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>

        {/* Informação sobre herança de grupo */}
        {structureState.controladoraId && (
          <Alert>
            <AlertDescription>
              Esta filial herda o vínculo ao grupo econômico da matriz.
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
